import React, { useState, useEffect } from 'react';
import { Pencil } from 'lucide-react';
import '../../component-styles/TaxSettings.css';
import API_BASE from '../../utils/api.js';
import notify from '../../utils/notify';

export default function TaxSettings() {
  const userId = localStorage.getItem('userId');
  const apiUrl = API_BASE;
  const idToken = localStorage.getItem('idToken');

  const [showModal, setShowModal] = useState(false);
  const [taxLocations, setTaxLocations] = useState([]);
  const [qbConnected, setQbConnected] = useState(true);
  const [savingTaxLocation, setSavingTaxLocation] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formState, setFormState] = useState({
    city: '',
    state: '',
    tax: '',
    agency: '',
    updated: '',
    quickbooksTaxCodeId: '',
    quickbooksTaxCodeName: '',
  });

  useEffect(() => {
    async function load() {
      const [settingsRes, taxCodesRes] = await Promise.all([
        fetch(`${apiUrl}/api/settings/${userId}`),
        fetch(`${apiUrl}/api/quickbooks/tax-codes`, {
          headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
        }).catch(() => null),
      ]);

      const data = await settingsRes.json();
      setTaxLocations(data.taxLocations || []);

      if (!taxCodesRes || taxCodesRes.status === 409 || !taxCodesRes.ok) {
        setQbConnected(false);
        return;
      }

      setQbConnected(true);
    }
    load();
  }, [apiUrl, userId, idToken]);

  function openAddModal() {
    setEditingIndex(null);
    setFormState({
      city: '',
      state: '',
      tax: '',
      agency: '',
      updated: '',
      quickbooksTaxCodeId: '',
      quickbooksTaxCodeName: '',
    });
    setShowModal(true);
  }

  function openEditModal(index) {
    setEditingIndex(index);
    const loc = taxLocations[index];
    setFormState({
      city: loc.city,
      state: loc.state,
      tax: loc.tax.replace('%', ''),
      agency: loc.agency,
      updated: loc.updated,
      quickbooksTaxCodeId: loc.quickbooksTaxCodeId || '',
      quickbooksTaxCodeName: loc.quickbooksTaxCodeName || '',
    });
    setShowModal(true);
  }

  async function saveToDB(updated) {
    await fetch(`${apiUrl}/api/settings/${userId}/tax`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ taxLocations: updated }),
    });
  }

  async function reloadQuickBooksTaxCodes() {
    const res = await fetch(`${apiUrl}/api/quickbooks/tax-codes`, {
      headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
    });

    if (!res.ok) {
      setQbConnected(false);
      return [];
    }

    const taxCodes = await res.json();
    setQbConnected(true);
    return Array.isArray(taxCodes) ? taxCodes : [];
  }

  function formatQuickBooksTaxCodeName(location = {}) {
    const state = String(location?.state || '').trim();
    const city = String(location?.city || '').trim();
    return city ? `${state} - ${city}` : state || 'InteriorFolio Tax';
  }

  async function ensureQuickBooksTaxCodeForLocation(locationPayload) {
    const res = await fetch(`${apiUrl}/api/quickbooks/tax-codes/create-from-location`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body: JSON.stringify({ location: locationPayload }),
    });

    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      await reloadQuickBooksTaxCodes();
      return {
        quickbooksTaxCodeId: data?.taxCode?.Id || '',
        quickbooksTaxCodeName: data?.taxCode?.Name || '',
      };
    }

    const errorMessage = String(data?.error || '');
    if (/duplicate|already exists/i.test(errorMessage)) {
      const refreshedCodes = await reloadQuickBooksTaxCodes();
      const expectedName = formatQuickBooksTaxCodeName(locationPayload).toLowerCase();
      const existing = refreshedCodes.find(
        (code) =>
          String(code?.Name || '')
            .trim()
            .toLowerCase() === expectedName
      );
      if (existing?.Id) {
        return {
          quickbooksTaxCodeId: existing.Id,
          quickbooksTaxCodeName: existing.Name || '',
        };
      }
    }

    throw new Error(data?.error || 'Failed to create QuickBooks tax code');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSavingTaxLocation(true);
    try {
      const locationPayload = {
        city: formState.city,
        state: formState.state,
        tax: formState.tax + '%',
        agency: formState.agency,
        updated: formState.updated,
      };

      let quickbooksTaxCodeId = formState.quickbooksTaxCodeId || '';
      let quickbooksTaxCodeName = formState.quickbooksTaxCodeName || '';
      const shouldAutoCreateQuickBooksTaxCode =
        qbConnected && (!quickbooksTaxCodeId || editingIndex === null);

      if (shouldAutoCreateQuickBooksTaxCode) {
        const createdCode = await ensureQuickBooksTaxCodeForLocation(locationPayload);
        quickbooksTaxCodeId = createdCode.quickbooksTaxCodeId || quickbooksTaxCodeId;
        quickbooksTaxCodeName = createdCode.quickbooksTaxCodeName || quickbooksTaxCodeName;
      }

      const newEntry = {
        ...locationPayload,
        quickbooksTaxCodeId,
        quickbooksTaxCodeName,
      };

      let updated;
      if (editingIndex !== null) {
        updated = [...taxLocations];
        updated[editingIndex] = newEntry;
      } else {
        updated = [...taxLocations, newEntry];
      }

      setTaxLocations(updated);
      await saveToDB(updated);
      setShowModal(false);
    } catch (err) {
      notify(err.message || 'Failed to save tax location');
    } finally {
      setSavingTaxLocation(false);
    }
  }

  return (
    <div className="taxset-container">
      <div className="taxset-header">
        <button className="taxset-add-btn" onClick={openAddModal}>
          + Add New
        </button>
      </div>

      <div className="taxset-table">
        <table>
          <thead>
            <tr>
              <th>City</th>
              <th>Sales Tax</th>
              <th>State</th>
              <th>Agency</th>
              <th>QB Tax Code</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {taxLocations.map((loc, idx) => (
              <tr key={idx}>
                <td>{loc.city}</td>
                <td>{loc.tax}</td>
                <td>{loc.state}</td>
                <td>{loc.agency}</td>
                <td>{loc.quickbooksTaxCodeName || 'Not mapped'}</td>
                <td>{loc.updated}</td>
                <td className="taxset-action">
                  <Pencil
                    size={14}
                    color="#007673"
                    onClick={() => openEditModal(idx)}
                    style={{ cursor: 'pointer' }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="taxset-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="taxset-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingIndex !== null ? 'Edit Tax Location' : 'New Tax Location'}</h3>

            <form className="taxset-form" onSubmit={handleSubmit}>
              <label>City</label>
              <input
                name="city"
                value={formState.city}
                onChange={(e) => setFormState({ ...formState, city: e.target.value })}
                required
              />

              <label>State</label>
              <input
                name="state"
                value={formState.state}
                onChange={(e) => setFormState({ ...formState, state: e.target.value })}
                required
              />

              <label>Sales Tax</label>
              <input
                name="tax"
                type="number"
                step="0.01"
                value={formState.tax}
                onChange={(e) => setFormState({ ...formState, tax: e.target.value })}
                required
              />

              <label>Agency</label>
              <input
                name="agency"
                value={formState.agency}
                onChange={(e) => setFormState({ ...formState, agency: e.target.value })}
                required
              />

              <label>Updated</label>
              <input
                type="date"
                name="updated"
                value={formState.updated}
                onChange={(e) => setFormState({ ...formState, updated: e.target.value })}
                required
              />

              <label>QuickBooks Tax Code</label>
              <div>
                <input
                  className="taxset-select"
                  value={
                    formState.quickbooksTaxCodeName ||
                    (qbConnected
                      ? 'Will be created automatically on save'
                      : 'Connect QuickBooks to auto-create tax code')
                  }
                  readOnly
                  disabled
                />
              </div>

              <div className="taxset-actions">
                <button
                  type="button"
                  className="taxset-cancel-btn"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>

                <button type="submit" className="taxset-save-btn" disabled={savingTaxLocation}>
                  {savingTaxLocation
                    ? 'Saving...'
                    : editingIndex !== null
                      ? 'Save Changes'
                      : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
